import { Box } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import { ESnapshot, EUser, exists } from 'lib/types';
import {
  getInvoicePaymentMethods,
  UserPaymentMethods
} from 'utils/userPaymentMethods';
import { logAndCaptureException } from 'utils';
import { PayInvoiceData } from 'lib/types/invoices';
import { ColumnService } from 'lib/services/directory';
import PayWithCreditCardButton from './buttons/PayWithCreditCardButton';
import { PayInvoicePaymentMethodsType } from '../PayInvoice';
import SavedPaymentMethods from './SavedPaymentMethods';
import { SavedPaymentsAccess } from '../helpers/getSavedPaymentsAccess';
import { getPreventLatePaymentValue } from '../helpers/preventLatePayment';

export type PayInvoicePaymentMethodsProps = {
  payInvoiceData: PayInvoiceData;
  savedPaymentsAccess: SavedPaymentsAccess;
  selectedPaymentMethod: PayInvoicePaymentMethodsType | undefined;
  selectedSavedMethodId: string | undefined;
  user: ESnapshot<EUser> | null;
  selectPaymentClickHandler: (
    method: PayInvoicePaymentMethodsType,
    selectedSavedMethodId?: string
  ) => void;
  setShowLoginModal: React.Dispatch<React.SetStateAction<string | undefined>>;
};

export function PayInvoicePaymentMethods({
  payInvoiceData,
  savedPaymentsAccess,
  selectedPaymentMethod,
  selectedSavedMethodId,
  user,
  selectPaymentClickHandler,
  setShowLoginModal
}: PayInvoicePaymentMethodsProps) {
  const { invoice } = payInvoiceData;
  const preventLatePayment = getPreventLatePaymentValue(payInvoiceData);

  const [userPaymentMethods, setUserPaymentMethods] =
    useState<UserPaymentMethods>();

  useEffect(() => {
    const loadSavedPaymentData = async (): Promise<void> => {
      if (!exists(user)) return;
      if (
        savedPaymentsAccess.disableACH &&
        savedPaymentsAccess.disableSavedCards
      )
        return;

      try {
        // Payment methods are scoped to the Stripe customer the invoice is
        // billed to, so users never see saved payment methods that cannot
        // actually pay this invoice.
        const res = await getInvoicePaymentMethods(invoice.id);
        setUserPaymentMethods(res);
      } catch (e) {
        logAndCaptureException(
          ColumnService.PAYMENTS,
          e,
          'Failed to load bank accounts and credit cards',
          { invoiceId: invoice.id, user: user.id }
        );
      }
    };
    void loadSavedPaymentData();
  }, [
    invoice.id,
    user?.id,
    savedPaymentsAccess.disableACH,
    savedPaymentsAccess.disableSavedCards
  ]);

  return (
    <>
      <Box>
        <PayWithCreditCardButton
          disabled={preventLatePayment}
          selected={selectedPaymentMethod === 'card'}
          clickHandler={selectPaymentClickHandler}
        />
      </Box>
      <SavedPaymentMethods
        payInvoiceData={payInvoiceData}
        userPaymentMethods={userPaymentMethods}
        savedPaymentsAccess={savedPaymentsAccess}
        user={user}
        selectPaymentClickHandler={selectPaymentClickHandler}
        selectedSavedMethodId={selectedSavedMethodId}
        setShowLoginModal={setShowLoginModal}
      />
    </>
  );
}
